import { ValidationError } from '.';

/**
 * Updates multiple form fields and clears associated validation errors
 * @param setForm - React setState function to update form state
 * @param setValidationErrors - React setState function to update validation errors
 * @returns Function that takes field paths and update function to modify form state
 * @example
 * const updateMultiple = updateFormMulti(setForm, setValidationErrors);
 * updateMultiple([['contact', 'name'], ['contact', 'email']], (prev) => ({
 *   ...prev,
 *   contact: { ...prev.contact, name: 'John', email: 'john@example.com' }
 * }));
 */
export const updateFormMulti =
  <T,>(
    setForm: React.Dispatch<React.SetStateAction<T>>,
    setValidationErrors: React.Dispatch<React.SetStateAction<ValidationError[]>>
  ) =>
  (fieldPartsList: string[][], update: (prev: T) => T) => {
    fieldPartsList.forEach((fieldParts) =>
      setValidationErrors((prev) =>
        prev.filter(
          (e) =>
            e.field.length !== fieldParts.length || !e.field.every((v, i) => v === fieldParts[i])
        )
      )
    );
    setForm((prev) => update(prev));
  };

/**
 * Updates a single form field and clears associated validation errors
 * @param setForm - React setState function to update form state
 * @param setValidationErrors - React setState function to update validation errors
 * @returns Function that takes field path and update function to modify form state
 * @example
 * const update = updateForm(setForm, setValidationErrors);
 * update(['contact', 'name'], (prev) => ({
 *   ...prev,
 *   contact: { ...prev.contact, name: 'John' }
 * }));
 */
export const updateForm =
  <T,>(
    setForm: React.Dispatch<React.SetStateAction<T>>,
    setValidationErrors: React.Dispatch<React.SetStateAction<ValidationError[]>>
  ) =>
  (fieldParts: string[], update: (prev: T) => T) => {
    setValidationErrors((prev) => prev.filter((e) => !getValidationError([e], fieldParts)));
    setForm((prev) => update(prev));
  };

/**
 * Gets validation error message for a specific field path.
 *
 * By default this performs a path-tail (suffix) match so that a nested
 * `Field` component querying with a short path like `['email']` can pick up
 * an error whose full path is `['contact', 'email']`. That's the right
 * behavior for the common case of nested form components.
 *
 * Pass `{ exact: true }` to require the error's full path to equal
 * `fieldParts`. Use this when a top-level field name would otherwise
 * collide with a nested error path under the same key — e.g. a top-level
 * `['title']` field that should NOT light up when the server returns a
 * `['sections', '0', 'title']` error against one of its child sections.
 *
 * @param validationErrors - Array of validation errors
 * @param fieldParts - Array of strings representing path to form field
 * @param options.exact - When true, require an exact full-path match
 * @returns Error message if validation error exists for field
 * @example
 * const errorMessage = getValidationError(validationErrors, ['contact', 'email']);
 * const topLevelTitleErr = getValidationError(validationErrors, ['title'], { exact: true });
 */
export const getValidationError = (
  validationErrors: ValidationError[],
  fieldParts: string[],
  options: { exact?: boolean } = {}
) =>
  validationErrors.find((e) => {
    if (options.exact) {
      return (
        e.field.length === fieldParts.length &&
        e.field.every((v, i) => v === fieldParts[i])
      );
    }
    return (
      e.field.length >= fieldParts.length &&
      e.field.slice(-fieldParts.length).every((v, i) => v === fieldParts[i])
    );
  })?.message;

/**
 * Hook that provides form update and validation error utilities
 * @param setForm - React setState function to update form state
 * @param setValidationErrors - React setState function to update validation errors
 * @returns Object containing form update and error checking functions
 * @example
 * const { updateForm, updateFormMulti, getError } = useFormActions(setForm, setValidationErrors);
 */
export const useFormActions = <T,>(
  setForm: React.Dispatch<React.SetStateAction<T>>,
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationError[]>>
) => ({
  updateForm: updateForm(setForm, setValidationErrors),
  updateFormMulti: updateFormMulti(setForm, setValidationErrors),
  getValidationError
});
